/*
 * Cart drawer - Way Sauce #932.
 *
 * Dawn 2.2.0 has no cart drawer, so this is a bespoke component. It deliberately does NOT
 * reuse cart.js: CartItems there is bound to the cart page by id (main-cart-items,
 * main-cart-footer) and touching it would put the live cart page at risk.
 *
 * It exposes setActiveElement / getSectionsToRender / renderContents so product-form.js can
 * talk to it exactly as it talks to cart-notification.
 */

if (!customElements.get('cart-drawer')) {
  customElements.define(
    'cart-drawer',
    class CartDrawer extends HTMLElement {
      constructor() {
        super();

        this.activeElement = null;
        this.cachedCardsHTML = null;
        this.cachedAnchorId = null;
        // the anchor the recommendations were built from. It is kept while that product is
        // still in the cart, because re-anchoring on every add swapped the whole strip out
        // from under whoever was reading it.
        this.pinnedAnchorId = null;
        this.stripScrollLeft = 0;
        this.lastFillPercent = null;

        this.addEventListener('click', this.onClick.bind(this));
        this.onKeyUp = this.handleKeyUp.bind(this);
        this.onStripScroll = this.updateArrows.bind(this);
        this.onResize = this.updateArrows.bind(this);
        window.addEventListener('resize', this.onResize);
      }

      connectedCallback() {
        this.bindCartIcon();
        this.loadRecommendations();
      }

      /* ---------- opening and closing ---------- */

      bindCartIcon() {
        const icon = document.getElementById('cart-icon-bubble');
        if (!icon || icon.dataset.cartDrawerBound === 'true') return;

        icon.dataset.cartDrawerBound = 'true';
        icon.addEventListener('click', (event) => {
          // the href stays a real link so it still works with JS off
          event.preventDefault();
          this.setActiveElement(icon);
          this.open();
        });
      }

      open() {
        // re-opened on every add while already open - only lock once
        if (!this.classList.contains('cart-drawer--open')) this.lockScroll();
        this.classList.add('cart-drawer--open');
        document.addEventListener('keyup', this.onKeyUp);

        const panel = this.querySelector('.cart-drawer__panel');
        if (panel) trapFocus(panel, panel);

        this.loadRecommendations();
      }

      close() {
        this.classList.remove('cart-drawer--open');
        this.unlockScroll();
        document.removeEventListener('keyup', this.onKeyUp);
        removeTrapFocus(this.activeElement);
      }

      /*
       * Same fix as quick-add.js: locking body overflow drops the scrollbar and slides the page
       * sideways (7px at 1440). Put equivalent padding in its place, measured before the lock.
       */
      lockScroll() {
        const scrollbar = window.innerWidth - document.documentElement.clientWidth;
        if (scrollbar > 0) {
          this.previousBodyPadding = document.body.style.paddingRight;
          document.body.style.paddingRight = `${scrollbar}px`;
        }
        document.body.classList.add('overflow-hidden');
      }

      unlockScroll() {
        document.body.classList.remove('overflow-hidden');
        if (this.previousBodyPadding !== undefined) {
          document.body.style.paddingRight = this.previousBodyPadding;
          this.previousBodyPadding = undefined;
        }
      }

      handleKeyUp(event) {
        if (event.code === 'Escape') this.close();
      }

      onClick(event) {
        const closer = event.target.closest('[data-cart-drawer-close]');
        if (closer) {
          event.preventDefault();
          this.close();
          return;
        }

        const quantityButton = event.target.closest('[data-cart-drawer-quantity]');
        if (quantityButton) {
          event.preventDefault();
          this.updateQuantity(
            quantityButton.dataset.line,
            parseInt(quantityButton.dataset.cartDrawerQuantity, 10)
          );
          return;
        }

        const addButton = event.target.closest('[data-cart-drawer-add]');
        if (addButton) {
          event.preventDefault();
          this.addVariant(addButton);
          return;
        }

        if (event.target.closest('[data-cross-sell-prev]')) {
          event.preventDefault();
          this.scrollStrip(-1);
          return;
        }

        if (event.target.closest('[data-cross-sell-next]')) {
          event.preventDefault();
          this.scrollStrip(1);
        }
      }

      /* ---------- the recommendation strip's arrows ---------- */

      scrollStrip(direction) {
        const strip = this.querySelector('[data-cross-sell-strip]');
        if (!strip) return;

        const card = strip.querySelector('.cart-drawer__card:not([hidden])');
        // a card plus its gap, so a click lands cards on the same edge every time
        const step = card ? card.getBoundingClientRect().width + 9 : strip.clientWidth * 0.8;

        strip.scrollBy({ left: step * direction, behavior: 'smooth' });
      }

      updateArrows() {
        const strip = this.querySelector('[data-cross-sell-strip]');
        const nav = this.querySelector('[data-cross-sell-nav]');
        const prev = this.querySelector('[data-cross-sell-prev]');
        const next = this.querySelector('[data-cross-sell-next]');
        if (!strip || !nav || !prev || !next) return;

        const overflowing = strip.scrollWidth - strip.clientWidth > 2;
        nav.hidden = !overflowing;
        if (!overflowing) return;

        prev.disabled = strip.scrollLeft <= 2;
        next.disabled = strip.scrollLeft >= strip.scrollWidth - strip.clientWidth - 2;
      }

      /* ---------- the product-form.js contract ---------- */

      setActiveElement(element) {
        this.activeElement = element;
      }

      getSectionsToRender() {
        return [
          { id: 'cart-drawer', selector: 'cart-drawer' },
          { id: 'cart-icon-bubble' },
        ];
      }

      renderContents(parsedState) {
        this.applySections(parsedState.sections);
        this.open();
      }

      getSectionInnerHTML(html, selector = '.shopify-section') {
        const parsed = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
        return parsed ? parsed.innerHTML : null;
      }

      /* ---------- cart mutations ---------- */

      updateQuantity(line, quantity) {
        if (Number.isNaN(quantity) || quantity < 0) return;

        this.setBusy(true);
        this.clearError();

        const body = JSON.stringify({
          line,
          quantity,
          sections: this.getSectionsToRender().map((section) => section.id),
          sections_url: window.location.pathname,
        });

        fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
          .then((response) => response.json())
          .then((parsedState) => {
            if (parsedState.status) {
              this.showError(parsedState.description || parsedState.message);
              return;
            }
            this.applySections(parsedState.sections);
          })
          .catch(() => {
            this.showError(window.cartStrings.error);
          })
          .finally(() => {
            this.setBusy(false);
          });
      }

      addVariant(button) {
        if (button.getAttribute('aria-disabled') === 'true') return;

        button.setAttribute('aria-disabled', 'true');
        const card = button.closest('.cart-drawer__card');
        if (card) card.classList.add('cart-drawer__card--adding');

        this.rememberStripScroll();
        this.setBusy(true);
        this.clearError();

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        config.body = JSON.stringify({
          id: button.dataset.cartDrawerAdd,
          quantity: 1,
          sections: this.getSectionsToRender().map((section) => section.id),
          sections_url: window.location.pathname,
        });

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((parsedState) => {
            if (parsedState.status) {
              this.showError(parsedState.description || parsedState.message);
              button.removeAttribute('aria-disabled');
              return;
            }
            this.applySections(parsedState.sections);
          })
          .catch(() => {
            this.showError(window.cartStrings.error);
            button.removeAttribute('aria-disabled');
          })
          .finally(() => {
            this.setBusy(false);
          });
      }

      /* ---------- re-rendering ---------- */

      rememberStripScroll() {
        const strip = this.querySelector('[data-cross-sell-strip]');
        if (strip) this.stripScrollLeft = strip.scrollLeft;
      }

      applySections(sections) {
        if (!sections) return;

        this.rememberStripScroll();

        const previousFill = this.querySelector('.cart-drawer__progress-fill');
        this.lastFillPercent = previousFill ? previousFill.style.width : null;

        const drawerHTML = sections['cart-drawer'];
        if (drawerHTML) {
          const parsed = new DOMParser().parseFromString(drawerHTML, 'text/html');
          const fresh = parsed.querySelector('cart-drawer');

          if (fresh) {
            // the data attributes carry the cart total, the gap target and what is already in
            // the cart, so the recommendation filter has to move with them
            Array.from(fresh.attributes).forEach((attribute) => {
              if (attribute.name === 'class') return;
              this.setAttribute(attribute.name, attribute.value);
            });

            // keep the open state, take everything else from the server render
            const wasOpen = this.classList.contains('cart-drawer--open');
            this.className = fresh.className;
            if (wasOpen) this.classList.add('cart-drawer--open');

            this.innerHTML = fresh.innerHTML;
          }
        }

        const iconHTML = sections['cart-icon-bubble'];
        const icon = document.getElementById('cart-icon-bubble');
        if (iconHTML && icon) {
          const inner = this.getSectionInnerHTML(iconHTML);
          if (inner !== null) icon.innerHTML = inner;
        }

        this.animateProgress();
        this.loadRecommendations();
      }

      // the server render arrives with the bar already at its new width, so it would jump.
      // Put it back where it was, then let the CSS transition carry it - the progress bar is
      // the whole point of the ticket, so it is worth seeing it move.
      animateProgress() {
        const fill = this.querySelector('.cart-drawer__progress-fill');
        if (!fill || this.lastFillPercent === null) return;

        const target = fill.style.width;
        if (target === this.lastFillPercent) return;

        fill.style.width = this.lastFillPercent;
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            fill.style.width = target;
          });
        });
      }

      setBusy(busy) {
        this.classList.toggle('cart-drawer--busy', busy);
      }

      showError(message) {
        const errors = this.querySelector('[data-cart-drawer-errors]');
        if (!errors) return;
        errors.textContent = message || window.cartStrings.error;
        errors.hidden = false;
      }

      clearError() {
        const errors = this.querySelector('[data-cart-drawer-errors]');
        if (!errors) return;
        errors.textContent = '';
        errors.hidden = true;
      }

      /* ---------- recommendations ---------- */

      cartProductIds() {
        return (this.dataset.cartProductIds || '').split(',').filter((id) => id.length > 0);
      }

      /*
       * Shopify puts the newest line first, so cart.items.first moves every time something is
       * added. Following it re-fetched a completely different set of products on every add and
       * threw away where the shopper had scrolled to. The anchor is pinned to whatever it was
       * first built from and only moves when that product leaves the cart.
       */
      resolveAnchorId() {
        const inCart = this.cartProductIds();

        if (this.pinnedAnchorId && inCart.includes(this.pinnedAnchorId)) {
          return this.pinnedAnchorId;
        }

        this.pinnedAnchorId = this.dataset.anchorProductId || null;
        return this.pinnedAnchorId;
      }

      loadRecommendations() {
        const container = this.querySelector('[data-cart-drawer-cross-sell]');
        if (!container) return;

        const anchorId = this.resolveAnchorId();
        if (!anchorId) {
          container.hidden = true;
          return;
        }

        if (this.cachedAnchorId === anchorId && this.cachedCardsHTML !== null) {
          this.applyRecommendations();
          return;
        }

        const url = `${this.dataset.recommendationsUrl}?section_id=cart-drawer-recommendations&product_id=${anchorId}&limit=${this.dataset.recommendationsLimit || 10}`;

        fetch(url)
          .then((response) => response.text())
          .then((text) => {
            const cards = new DOMParser()
              .parseFromString(text, 'text/html')
              .querySelector('.cart-drawer__cross-sell-cards');

            this.cachedCardsHTML = cards ? cards.innerHTML : '';
            this.cachedAnchorId = anchorId;
            this.applyRecommendations();
          })
          .catch(() => {
            container.hidden = true;
          });
      }

      applyRecommendations() {
        const container = this.querySelector('[data-cart-drawer-cross-sell]');
        const strip = this.querySelector('[data-cross-sell-strip]');
        if (!container || !strip || this.cachedCardsHTML === null) return;

        strip.innerHTML = this.cachedCardsHTML;
        strip.removeEventListener('scroll', this.onStripScroll);
        strip.addEventListener('scroll', this.onStripScroll, { passive: true });

        const inCart = this.cartProductIds();

        const gap = parseInt(this.dataset.progressTarget, 10) - parseInt(this.dataset.cartTotal, 10);
        const cards = Array.from(strip.querySelectorAll('.cart-drawer__card'));

        const candidates = cards.filter((card) => !inCart.includes(card.dataset.productId));
        cards.forEach((card) => {
          card.hidden = !candidates.includes(card);
        });

        // below the threshold the strip only earns its heading if the products in it actually
        // close the gap - otherwise the copy would be promising something it cannot deliver
        let closesGap = [];
        if (gap > 0) {
          closesGap = candidates.filter((card) => parseInt(card.dataset.price, 10) >= gap);
          if (closesGap.length > 0) {
            candidates.forEach((card) => {
              card.hidden = !closesGap.includes(card);
            });
          }
        }

        const note = this.querySelector('[data-cross-sell-note]');
        if (note) note.hidden = gap > 0 && closesGap.length === 0;

        const visible = strip.querySelectorAll('.cart-drawer__card:not([hidden])');
        container.hidden = visible.length === 0;

        // a short stagger so the strip arrives rather than snapping in
        visible.forEach((card, index) => {
          card.style.setProperty('--card-delay', `${Math.min(index, 6) * 40}ms`);
        });

        // put the shopper back where they were before the cart change
        strip.scrollLeft = this.stripScrollLeft;
        this.updateArrows();
      }
    }
  );
}
